/*
 * AdvaHealth Solutions Pty. Ltd. ("AHS") CONFIDENTIAL
 * Copyright (c) 2022 AdvaHealth Solutions Pty. Ltd. All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains the property of AHS. The intellectual and technical concepts contained
 * herein are proprietary to AHS and are protected by trade secret or copyright law.
 * Dissemination of this information or reproduction of this material is strictly forbidden unless prior written permission is obtained
 * from AHS.  Access to the source code contained herein is hereby forbidden to anyone except current AHS employees, managers or contractors who have executed
 * Confidentiality and Non-disclosure agreements explicitly covering such access.
 *
 * The copyright notice above does not evidence any actual or intended publication or disclosure  of  this source code, which includes
 * information that is confidential and/or proprietary, and is a trade secret, of AHS. ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC  PERFORMANCE,
 * OR PUBLIC DISPLAY OF OR THROUGH USE  OF THIS  SOURCE CODE  WITHOUT  THE EXPRESS WRITTEN CONSENT OF COMPANY IS STRICTLY PROHIBITED, AND IN VIOLATION OF APPLICABLE
 * LAWS AND INTERNATIONAL TREATIES.  THE RECEIPT OR POSSESSION OF  THIS SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS
 * TO REPRODUCE, DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT  MAY DESCRIBE, IN WHOLE OR IN PART.
 */

import { DateRange, SearchPaginateType } from "@advapacs/common";
import { DAYS } from "../constant";
import { DropDownResponse, OptionTypeBase } from "./common";
import { Issuer } from "./issuer";

export interface IGateways {
  id: string;
  version: string;
  name: string;
  lastActivityAt: string;
  online: boolean;
  createdAt?: string;
  updatedAt?: string;
  enabled: boolean;
  pushConnected: boolean;
  osType?: string;
  clockSkewMillis?: number;
}

export interface IDicomWebConfiguration {
  proxyPort: number;
  allowAnonymous: boolean;
  defaultIssuerOfPatientId?: Issuer;
  defaultIssuerOfAccessionNumber?: Issuer;
  defaultDataSet?: DropDownResponse<string>;
}

export interface IDicomWebConfigurationState {
  proxyPort: number;
  allowAnonymous: boolean;
  defaultIssuerOfPatientIdOption?: OptionTypeBase;
  defaultIssuerOfAccessionNumberOption?: OptionTypeBase;
  defaultDataSetId?: string;
}

export interface IDimseTimeouts {
  connectTimeoutSeconds: number | null;
  responseTimeoutSeconds: number | null;
  requestTimeoutSeconds: number | null;
  idleTimeoutSeconds: number | null;
  acceptTimeoutSeconds: number | null;
  releaseTimeoutSeconds: number | null;
}

export interface IGateway {
  id: string;
  name: string;
  uploadThreads: number | null;
  downloadThreads: number | null;
  uploadCompression: boolean;
  version?: string;
  autoUpdateSchedule?: {
    daysOfWeek: string[];
    rangeDateTime: DateRange;
  };
  whitelistIps: string;
  enabled: boolean;
  dicomWeb: IDicomWebConfiguration;
  osType?: string;
  localIps?: string[];
  dimseTimeouts?: IDimseTimeouts;
}

export interface IGatewayState {
  id: string;
  name: string;
  uploadThreads: number | null;
  downloadThreads: number | null;
  uploadCompression: boolean;
  whitelistIps: string;
  enabled: boolean;
  dicomWeb: IDicomWebConfigurationState;
  autoUpdateSchedule?: {
    daysOfWeek: string[];
    rangeDateTime: DateRange;
  };
  enabledDicomWeb?: boolean;
  osType?: string;
  dataDirectory?: string;
  dimseTimeouts: IDimseTimeouts;
}

export interface IDicomWebConfigurationUpdate {
  proxyPort: number;
  allowAnonymous: boolean;
  defaultIssuerOfPatientIdId?: string;
  defaultIssuerOfAccessionNumberId?: string;
  defaultDataSetId?: string;
}
export interface IUpdateGateway {
  name: string;
  uploadThreads: number | null;
  downloadThreads: number | null;
  uploadCompression: boolean;
  whitelistIps: string;
  enabled: boolean;
  dicomWeb: IDicomWebConfigurationUpdate;
  autoUpdateSchedule?: {
    daysOfWeek: string[];
    rangeTime: {
      startTime?: string | null;
      endTime?: string | null;
    };
  };
  dataDirectory?: string;
  dimseTimeouts?: IDimseTimeouts;
}

export interface IGatewayList {
  content: IGateways[];
}

export interface RegionResponse {
  code: string;
  region: string;
}

export interface IAccessKeyResponse {
  accessKeyId: string;
  accessKeySecret: string;
  region: RegionResponse;
}

export interface IGatewaySearch extends SearchPaginateType {
  status?: boolean;
  searchKeyword?: string;
}

export interface IGatewayInstaller {
  id: string;
  identifier: string;
  downloadUrl: string;
  version: string;
  repository: string;
  format: string;
  os: string;
  name: string;
}

export interface IGatewayInstallers {
  content: IGatewayInstaller[];
}

export interface IGatewayInstallerLink {
  url: string;
}

export const defaultValueDicomWebConfiguration: Readonly<IDicomWebConfigurationState> =
  {
    proxyPort: 0,
    allowAnonymous: false,
  };

export const DIMSE_TIMEOUT_MIN_SECONDS = 5;
export const DIMSE_TIMEOUT_MAX_SECONDS = 300;

export const defaultValueDimseTimeouts: Readonly<IDimseTimeouts> = {
  connectTimeoutSeconds: 15,
  responseTimeoutSeconds: 15,
  requestTimeoutSeconds: 15,
  idleTimeoutSeconds: 20,
  acceptTimeoutSeconds: 10,
  releaseTimeoutSeconds: 10,
};

const currentDate = new Date();

export const defaultSchedule = {
  daysOfWeek: [
    DAYS.MONDAY,
    DAYS.TUESDAY,
    DAYS.WEDNESDAY,
    DAYS.THURSDAY,
    DAYS.FRIDAY,
    DAYS.SATURDAY,
    DAYS.SUNDAY,
  ],
  rangeDateTime: {
    startDate: new Date(currentDate.setHours(0, 0, 0, 0)),
    endDate: new Date(currentDate.setHours(3, 0, 0, 0)),
  },
};

export const defaultValueGateway: Readonly<IGateway> = {
  id: "",
  name: "",
  uploadThreads: null,
  downloadThreads: null,
  uploadCompression: true,
  whitelistIps: "",
  enabled: true,
  dicomWeb: defaultValueDicomWebConfiguration,
  autoUpdateSchedule: defaultSchedule,
  dimseTimeouts: defaultValueDimseTimeouts,
};

export const defaultValueGatewayState: Readonly<IGatewayState> = {
  id: "",
  name: "",
  uploadThreads: null,
  downloadThreads: null,
  uploadCompression: true,
  whitelistIps: "",
  enabled: true,
  dicomWeb: defaultValueDicomWebConfiguration,
  enabledDicomWeb: false,
  autoUpdateSchedule: defaultSchedule,
  osType: "",
  dimseTimeouts: defaultValueDimseTimeouts,
};

export const defaultValueGateways: Readonly<IGateways> = {
  id: "",
  version: "",
  name: "",
  lastActivityAt: "",
  online: false,
  enabled: true,
  pushConnected: false,
  osType: "",
};

export const defaultValueAccessKeyResponse: Readonly<IAccessKeyResponse> = {
  accessKeyId: "",
  accessKeySecret: "",
  region: {
    region: "",
    code: "",
  },
};

export interface IGatewayLogSearch extends SearchPaginateType {
  startDate?: Date;
  endDate?: Date;
  searchKeyword?: string;
  gatewayId?: string;
}

export interface IGatewayLog {
  id: string;
  message: string;
  createdAt: string;
  level: string;
  className: string;
}
